import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  CircularProgress, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  Typography,
  Paper,
  Alert,
  Slide,
  Avatar,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import {
  Shield as ShieldIcon,
  QrCode as QrCodeIcon,
  Key as KeyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { generateTwoFactorSecret, enableTwoFactor, disableTwoFactor } from '../../store/slices/authSlice';
import { AppDispatch } from '../../store';

interface TwoFactorSetupProps {
  open: boolean;
  onClose: () => void;
  isEnabled: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function TwoFactorSetup({ open, onClose, isEnabled }: TwoFactorSetupProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = ['Сканировать QR-код', 'Ввести код подтверждения'];
  
  // Генерация секрета при открытии модального окна
  useEffect(() => {
    if (open && !isEnabled) {
      handleGenerateSecret();
    }
  }, [open, isEnabled]);

  const handleGenerateSecret = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await dispatch(generateTwoFactorSecret()).unwrap();
      setQrCode(result.qrCodeUrl);
      setSecret(result.secret);
      setActiveStep(0);
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка при генерации секрета');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEnableTwoFactor = async () => {
    if (!verificationCode) {
      setError('Пожалуйста, введите код подтверждения');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await dispatch(enableTwoFactor(verificationCode)).unwrap();
      setSuccess('Двухфакторная аутентификация успешно включена');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка при включении 2FA');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDisableTwoFactor = async () => {
    if (!verificationCode) {
      setError('Пожалуйста, введите код подтверждения');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      await dispatch(disableTwoFactor(verificationCode)).unwrap();
      setSuccess('Двухфакторная аутентификация успешно отключена');
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Произошла ошибка при отключении 2FA');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleClose = () => {
    setVerificationCode('');
    setError(null);
    setSuccess(null);
    setQrCode(null);
    setSecret(null);
    setActiveStep(0);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: isEnabled 
              ? 'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)'
              : 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
          }
        }
      }}
    >
      <DialogTitle sx={{ pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: isEnabled ? 'rgba(245, 87, 108, 0.1)' : 'rgba(102, 126, 234, 0.1)',
              color: isEnabled ? '#f5576c' : '#667eea',
              width: 56,
              height: 56,
            }}
          >
            <ShieldIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {isEnabled ? 'Отключение 2FA' : 'Настройка 2FA'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 2 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2, borderRadius: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert 
            severity="success" 
            icon={<CheckIcon />}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {success}
          </Alert>
        )}
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!isEnabled && (
              <>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: 'rgba(102, 126, 234, 0.05)',
                    borderRadius: 2,
                    border: '1px solid rgba(102, 126, 234, 0.2)',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                    Сканируйте QR-код с помощью приложения Google Authenticator или введите секретный ключ вручную.
                  </Typography>
                  
                  {qrCode && (
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      mb: 2,
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 2,
                    }}>
                      <img 
                        src={qrCode} 
                        alt="QR Code for 2FA" 
                        style={{ 
                          width: 200, 
                          height: 200,
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          padding: 8
                        }}
                      />
                    </Box>
                  )}
                  
                  {secret && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: 'white',
                        borderRadius: 2,
                        border: '1px dashed #667eea',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <KeyIcon sx={{ color: '#667eea', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                          Секретный ключ:
                        </Typography>
                      </Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          wordBreak: 'break-all',
                          fontFamily: 'monospace',
                          bgcolor: 'rgba(102, 126, 234, 0.05)',
                          p: 1.5,
                          borderRadius: 1,
                          fontWeight: 600,
                          letterSpacing: 1,
                        }}
                      >
                        {secret}
                      </Typography>
                    </Paper>
                  )}
                </Paper>
              </>
            )}
            
            {isEnabled && (
              <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
                Для отключения двухфакторной аутентификации введите код из вашего приложения.
              </Alert>
            )}
            
            <TextField
              fullWidth
              label="Код подтверждения"
              variant="outlined"
              value={verificationCode}
              onChange={(e) => {
                setVerificationCode(e.target.value);
                setError(null);
                if (!isEnabled && e.target.value) {
                  setActiveStep(1);
                }
              }}
              placeholder="Введите 6-значный код"
              inputProps={{
                maxLength: 6,
                style: { 
                  textAlign: 'center', 
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem',
                  fontWeight: 600,
                }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            px: 3,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Отмена
        </Button>
        {!isEnabled ? (
          <Button 
            onClick={handleEnableTwoFactor} 
            variant="contained"
            disabled={isLoading || !verificationCode || verificationCode.length !== 6}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 6px 16px rgba(102, 126, 234, 0.5)',
              },
              '&:disabled': {
                background: 'rgba(0,0,0,0.12)',
              }
            }}
          >
            Включить
          </Button>
        ) : (
          <Button 
            onClick={handleDisableTwoFactor} 
            variant="contained"
            color="error"
            disabled={isLoading || !verificationCode || verificationCode.length !== 6}
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(245, 87, 108, 0.4)',
              '&:hover': {
                boxShadow: '0 6px 16px rgba(245, 87, 108, 0.5)',
              }
            }}
          >
            Отключить
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}